import { ExternalLinkIcon, inforOrangeIcon } from '@/shared/ui/assets';
import { NormalButton, Chip, CustomButton } from '@/shared/ui/components';
import { DOUBLE_HYPHEN_BULLET, FLAGGED_REASON } from '@/shared/config';
import { generateIssueFound, STUDY_STATUS, STUDY_PENDING_COMMAND } from '@/entities/study';
import {
  getDuration,
  zeroPad,
  checkToFormatPhoneNumber,
  formatPhoneNumber,
  generateFullName,
  addTimezoneToMoment,
  convertToMoment,
  dateToString,
  gettextDeviceNotificationHistory,
  gettextStudyStatus,
} from '@/shared/lib';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { UndoMarkAddressed } from '@/pages/studies-list/@x/study-management';
import { generateDefaultStudySetting } from './editStudyDetails';
import type { LinkedStudy } from '../ui/studyDetailsTab/studyDetailsTab.types';

momentDurationFormatSetup(moment);

/**
 * Renders one label/value row of the study details panels. Returns nothing for an empty data object, otherwise prints the label with a trailing colon and the value, falling back to the double-hyphen bullet, under an optional extra class taken from data.valueClassName.
 *
 * @param data - The row descriptor with label, value and optional valueClassName.
 * @param i - Optional React key for the row.
 * @returns The rendered row, or a falsy value for empty data.
 */
const renderRowItem = (data: any, i?: string) =>
  !_.isEmpty(data) && (
    <div className="row-item" key={i}>
      <div className="label">{`${data.label}:`}</div>
      <div className={classNames('value', data.valueClassName)}>
        {data.value || DOUBLE_HYPHEN_BULLET}
      </div>
    </div>
  );

/**
 * Renders the badge for one slot of a follow-on study pair. Shows a grey Not-started chip when no linked study exists, an orange Cancelled chip when that slot's follow-on plan was cancelled, a blue This-study chip when the linked study's friendlyId matches the current one, and otherwise an Open button that opens `/studies/{id}` in a new browser tab.
 *
 * @param friendlyId - The friendly id of the study being viewed.
 * @param linkedStudy - The linked study for this slot, possibly empty.
 * @param cancelledReason - Reason the follow-on study was cancelled, absent while the plan is still live.
 * @returns The chip or open-button badge for the slot.
 */
const renderFoList = (friendlyId: string, linkedStudy: LinkedStudy, cancelledReason?: string) => {
  if (_.isEmpty(linkedStudy)) {
    const { background, color, label } = cancelledReason
      ? { background: '#FFF4E5', color: '#F58F0A', label: t`Cancelled` }
      : { background: '#EAEDF1', color: '#444F66', label: t`Not started` };
    return (
      <div className="badge">
        <Chip
          background={background}
          style={{ border: 'unset', paddingInline: 8 }}
          color={color}
          closable={false}
          className="custom-chip"
        >
          {label}
        </Chip>
      </div>
    );
  }
  if (friendlyId === linkedStudy.friendlyId) {
    return (
      <div className="badge">
        <Chip
          background="#D9E7FA"
          style={{ border: 'unset', paddingInline: 8 }}
          color="#2265C6"
          closable={false}
          className="custom-chip"
        >
          {t`This study`}
        </Chip>
      </div>
    );
  }
  return (
    <div className="badge">
      <CustomButton
        id="open-btn"
        onClick={() => {
          window.open(`/studies/${linkedStudy.id}`);
        }}
        size="small"
        style="default"
        type="secondary"
        isRightIcon
        icon={<ReactSVG src={ExternalLinkIcon} {...({ alt: 'External link icon' } as any)} />}
      >
        {t`Open`}
      </CustomButton>
    </div>
  );
};

/**
 * Renders the two-row follow-on study summary of the study details tab. Takes the first and second linkedStudies, falling back to the study itself and undefined, prints zero-padded study ids and study types while preferring the pending command's studySetting type for the study being updated, shows the follow-on duration through getDuration when no linked study exists yet, and appends renderFoList's badge to each row.
 *
 * @param data - The study, read for id, friendlyId, studyType, info, linkedStudies and pendingCommand.
 * @returns The follow-on study summary block.
 */
const foStudyContent = (data: any) => {
  const { id, friendlyId, studyType, info, linkedStudies, pendingCommand } = data;
  const { studyType: studyTypeTemp } = pendingCommand?.info?.studySetting || {};
  const firstStudy = linkedStudies?.length > 0 ? linkedStudies[0] : data;
  const secondStudy = linkedStudies?.length > 0 ? linkedStudies[1] : undefined;
  return (
    <div className="f0-content">
      <div className="study">
        <div className="study-row">
          <div className="item">
            <div className="label">{`${t`Study ID`}:`}</div>
            <div className="value">
              {linkedStudies?.length > 0
                ? zeroPad(linkedStudies[0].friendlyId)
                : zeroPad(friendlyId)}
            </div>
          </div>
          <div className="item">
            <div className="label">{`${t`Study type`}:`}</div>
            <div className="value">
              {linkedStudies?.length > 0
                ? linkedStudies[0].id === id && studyTypeTemp
                  ? studyTypeTemp
                  : linkedStudies[0].studyType
                : studyTypeTemp || studyType}
            </div>
          </div>
        </div>
        {renderFoList(friendlyId, firstStudy)}
      </div>
      <div className="study">
        <div className="study-row">
          <div className="item">
            <div className="label">
              {`${linkedStudies?.length > 0 ? t`Study ID` : t`Study duration`}:`}
            </div>
            <div className="value">
              {linkedStudies?.length > 0
                ? zeroPad(linkedStudies[1].friendlyId)
                : getDuration(
                    !_.isEmpty(pendingCommand?.info?.studySetting?.followOnStudy)
                      ? pendingCommand?.info?.studySetting?.followOnStudy?.duration
                      : info?.followOnStudy?.duration,
                    'd[d] hh[h] mm[m]',
                    'h',
                  )}
            </div>
          </div>
          <div className="item">
            <div className="label">{`${t`Study type`}:`}</div>
            <div className="value">
              {!_.isEmpty(pendingCommand?.info?.studySetting?.followOnStudy)
                ? pendingCommand?.info?.studySetting?.followOnStudy?.studyType
                : linkedStudies?.length > 0
                  ? linkedStudies[1].id === id && studyTypeTemp
                    ? studyTypeTemp
                    : linkedStudies[1].studyType
                  : info?.followOnStudy?.studyType}
            </div>
          </div>
        </div>
        {renderFoList(friendlyId, secondStudy, info?.followOnStudy?.cancelledReason)}
      </div>
    </div>
  );
};

/**
 * Renders the read-only referring-physician rows of the study details tab. Builds the name through generateFullName plus day phone, after-hours phone, email and fax, applying formatPhoneNumber only when checkToFormatPhoneNumber accepts that physician's contact country, then maps each pair through renderRowItem.
 *
 * @param data - The study, read for info.referringPhysician.
 * @returns One rendered row per referring-physician field.
 */
const generateReferringPhysician = (data: any) => {
  const { info } = data;
  const { referringPhysician } = info || {};
  /**
   * Whether the referring physician's phone numbers should be reformatted for display. Derived from checkToFormatPhoneNumber applied to that physician's contact country.
   *
   * @returns True when the contact country uses formatted phone numbers.
   */
  const shouldFormatReferringPhysicianPhoneNumber = checkToFormatPhoneNumber(
    referringPhysician?.contact?.country,
  );
  const arrayItem = [
    {
      label: t`Name`,
      value: generateFullName(referringPhysician),
    },
    {
      label: t`Day phone`,
      value:
        (shouldFormatReferringPhysicianPhoneNumber
          ? formatPhoneNumber(referringPhysician?.contact?.phone1)
          : referringPhysician?.contact?.phone1) || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`After hours phone`,
      value:
        (shouldFormatReferringPhysicianPhoneNumber
          ? formatPhoneNumber(referringPhysician?.contact?.nightPhone)
          : referringPhysician?.contact?.nightPhone) || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Email`,
      value: referringPhysician?.email || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Fax number`,
      value:
        (shouldFormatReferringPhysicianPhoneNumber
          ? formatPhoneNumber(referringPhysician?.contact?.fax)
          : referringPhysician?.contact?.fax) || DOUBLE_HYPHEN_BULLET,
    },
  ];
  return arrayItem.map((content) => renderRowItem(content));
};

/**
 * Renders the read-only interpreting-physician rows of the study details tab. Builds the name through generateFullName plus day phone, after-hours phone, email and fax, applying formatPhoneNumber only when checkToFormatPhoneNumber accepts that physician's contact country, then maps each pair through renderRowItem.
 *
 * @param data - The study, read for info.interprettingPhysician.
 * @returns One rendered row per interpreting-physician field.
 */
const generateInterpretingPhysician = (data: any) => {
  const { info } = data;
  const { interprettingPhysician } = info || {};
  /**
   * Whether the interpreting physician's phone numbers should be reformatted for display. Derived from checkToFormatPhoneNumber applied to that physician's contact country.
   *
   * @returns True when the contact country uses formatted phone numbers.
   */
  const shouldFormatInterprettingPhysicianPhoneNumber = checkToFormatPhoneNumber(
    interprettingPhysician?.contact?.country,
  );
  const arrayItem = [
    {
      label: t`Name`,
      value: generateFullName(interprettingPhysician),
    },
    {
      label: t`Day phone`,
      value:
        (shouldFormatInterprettingPhysicianPhoneNumber
          ? formatPhoneNumber(interprettingPhysician?.contact?.phone1)
          : interprettingPhysician?.contact?.phone1) || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`After hours phone`,
      value:
        (shouldFormatInterprettingPhysicianPhoneNumber
          ? formatPhoneNumber(interprettingPhysician?.contact?.nightPhone)
          : interprettingPhysician?.contact?.nightPhone) || DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Email`,
      value: interprettingPhysician?.email || DOUBLE_HYPHEN_BULLET,
      isBreakAll: true,
    },
    {
      label: t`Fax number`,
      value:
        (shouldFormatInterprettingPhysicianPhoneNumber
          ? formatPhoneNumber(interprettingPhysician?.contact?.fax)
          : interprettingPhysician?.contact?.fax) || DOUBLE_HYPHEN_BULLET,
    },
  ];
  return arrayItem.map((content) => renderRowItem(content));
};

/**
 * Renders the read-only diagnosis rows of the study details tab. Builds primary and secondary diagnosis code pairs plus patient medication from info.diagnosis, defaulting to the double-hyphen bullet and tagging the medication value with its own class, then maps each pair through renderRowItem.
 *
 * @param data - The study, read for info.diagnosis.
 * @returns One rendered row per diagnosis field.
 */
const generateDiagnosis = (data: any) => {
  const { info } = data;
  const { diagnosis } = info || {};
  const arrayItem = [
    {
      label: t`Primary diagnosis code`,
      value: diagnosis?.primary ? diagnosis?.primary : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Secondary diagnosis code`,
      value: info?.diagnosis?.second ? info?.diagnosis?.second : DOUBLE_HYPHEN_BULLET,
    },
    {
      label: t`Patient medication`,
      value: diagnosis?.patientMedications || DOUBLE_HYPHEN_BULLET,
      valueClassName: 'patient-medication',
    },
  ];
  return arrayItem.map((content) => renderRowItem(content));
};

/**
 * Groups a study or device history into per-day timeline entries for the study details tab.
 * Walks the history newest-first, converts each time to the study's local day, translates
 * charger and study or notification statuses, appends the battery level when non-zero, and marks
 * each item seen when it predates lastSeen.
 *
 * @param props - History input, lastSeen, timezone, studyTimeOffset and the history type.
 * @returns The per-day timeline entries, empty when the input is empty.
 */
const createTimelineData = ({
  input,
  lastSeen,
  timezone,
  studyTimeOffset,
  type,
  startStudyDate,
  facilityCountry,
  facilityTimezone,
}: {
  input: any[];
  lastSeen: string;
  timezone: string;
  studyTimeOffset: number;
  type: string;
  startStudyDate?: string;
  facilityCountry?: string;
  facilityTimezone?: string;
}): any => {
  if (input.length === 0) {
    return [];
  }

  const output = [];
  const clonedInput = [...input];
  _.reverse(clonedInput);
  for (let i = 0; i < clonedInput.length; i += 1) {
    const item = clonedInput[i];
    // item.time is a valid ISO string from the study/device history, so convertToMoment never returns null here
    const currentStartDate = convertToMoment({
      startStudyDate,
      isoDate: item.time,
      timezone,
      studyTimeOffset,
      facilityCountry,
      facilityTimezone,
    })!.startOf('day');
    const date = currentStartDate.format('YYYY-M-D');
    const seen = Date.parse(lastSeen) > Date.parse(item.time);
    const currentMoment = dayjs(item.time);
    if (!_.find(output, (x) => x.date === date)) {
      output.push({
        display: currentStartDate.format('MM/DD/YYYY'),
        data: [] as any[],
        date,
        time: item.time,
      });
    }
    /**
     * The already-accumulated output entry whose date matches the date being processed. Looked up in the output collection by date equality.
     *
     * @returns The matching output entry, or undefined when the date is new.
     */
    // just ensured above that an entry for this date exists in output
    const ele = _.find(output, (x) => x.date === date)!;
    let formattedStatus =
      item.status === 'Battery Charging'
        ? t`Charger Connected`
        : item.status === 'Battery Discharging'
          ? t`Charger Disconnected`
          : item.status;
    switch (type) {
      case 'studyHistory': {
        formattedStatus = gettextStudyStatus(formattedStatus);
        break;
      }
      case 'deviceNotificationHistory': {
        formattedStatus = gettextDeviceNotificationHistory(formattedStatus);
        break;
      }
      default: {
        break;
      }
    }
    ele.data.push({
      status:
        item.batteryLevel && parseInt(item.batteryLevel, 10) !== 0
          ? `${formattedStatus} (${item.batteryLevel}%)`
          : formattedStatus,
      time: currentMoment,
      seen,
    });
  }
  return output;
};

/**
 * Renders the grouped history timeline of the study details tab.
 * Emits one block per day with its display date, and under it each status with its time
 * formatted as HH:mm:ss in the study's timezone, offset and facility country.
 *
 * @param props - Grouped histories plus startStudyDate, timezone, studyTimeOffset and
 * facilityCountry.
 * @returns The rendered history blocks.
 */
const generateHistoryData = ({
  histories,
  startStudyDate,
  timezone,
  studyTimeOffset,
  facilityCountry,
  facilityTimezone,
}: {
  histories: any[];
  startStudyDate: string;
  timezone: string;
  studyTimeOffset: number;
  facilityCountry: string;
  facilityTimezone?: string;
}) =>
  histories.map((history) => (
    <div className="history" key={history.date}>
      <div className="date">{history.display}</div>
      <div className="milestone">
        {history.data.map((timestone: any) => (
          <div className="time-stone">
            <div className="time">
              {dateToString({
                startStudyDate,
                isoDate: timestone.time,
                timezone,
                studyTimeOffset,
                facilityCountry,
                facilityTimezone,
                format: 'HH:mm:ss',
              })}
            </div>
            <div className="status">{timestone.status}</div>
          </div>
        ))}
      </div>
    </div>
  ));

/**
 * Renders the artifact-percentage badge of the study details tab. Returns the double-hyphen bullet for a nil value, otherwise classes the number as potentially-lost from 70 up, need-attention from 50 up, or normal, and prints the matching warning label under the number for the two elevated bands.
 *
 * @param percentage - The study's total artifact percentage.
 * @returns The styled percentage badge, or the double-hyphen bullet.
 */
const artifactPercentageRenderer = (percentage: number) => {
  if (_.isNil(percentage)) {
    return DOUBLE_HYPHEN_BULLET;
  }

  const isPotentiallyLost = percentage >= 70;
  const isNeedAttention = percentage >= 50;
  return (
    <div
      className={classNames(
        isPotentiallyLost
          ? 'potentially-lost'
          : isNeedAttention
            ? 'need-attention'
            : 'normal-percent',
      )}
    >
      <strong className="percent">{`${percentage}%`}</strong>
      {(isPotentiallyLost || isNeedAttention) && (
        <div className="percent-label">
          {isPotentiallyLost ? 'Potentially lost' : 'Need attention'}
        </div>
      )}
    </div>
  );
};

/**
 * Renders the study-issue panel of the study details tab. A PENDING_UPLOAD flag yields a single offline-device upload row with a View-solutions button, otherwise a last-6-hours issue row is added only while the study is unfinished and its flag was resolved by a user, followed by the common issue from generateIssueFound and the artifactPercentageRenderer badge, with an UndoMarkAddressed control prefixed when the matching skip was recorded.
 *
 * @param data - The study, read for artifact, status, device, flaggedStudy, flaggedStudySkip and id.
 * @param toggleDisplaySolutionsDrawer - Opens the solutions drawer from the issue rows.
 * @param isHideMarkAddressedStudy - When true, hides the solutions button and the undo-mark-addressed control.
 * @returns The study-issue content block.
 */
const generateStudyIssue = (
  data: any,
  toggleDisplaySolutionsDrawer: () => void,
  isHideMarkAddressedStudy: boolean,
) => {
  const { artifact, status, device, flaggedStudy, flaggedStudySkip, id } = data;
  const { reason, resolvedAt } = flaggedStudy || {};
  const { ongoing, endOfDuration } = flaggedStudySkip || {};
  const isSkipPendingUpload =
    !!endOfDuration?.skippedAt && !!resolvedAt && !isHideMarkAddressedStudy;
  const isSkipArtifact = !!ongoing?.skippedAt && !!resolvedAt && !isHideMarkAddressedStudy;
  if (reason === FLAGGED_REASON.PENDING_UPLOAD) {
    const arr = [
      {
        label: t`Common issue found in study`,
        value: (
          <div className="last-issue-found-container">
            <div>Data upload pending due to offline device</div>
            {!isHideMarkAddressedStudy && (
              <NormalButton
                id="display-solution-modal-btn"
                className="view-solutions-btn"
                isText
                buttonName={t`View solutions`}
                onClick={toggleDisplaySolutionsDrawer}
              />
            )}
          </div>
        ),
      },
    ];

    return (
      <div className="content">
        {isSkipPendingUpload && <UndoMarkAddressed studyId={id} />}
        {arr.map((content) => renderRowItem(content))}
      </div>
    );
  }
  const arrayItem = [];
  const isEndOfDuration = dayjs().isAfter(dayjs(data?.stop));
  const hideFlagIssue = !flaggedStudy?.resolvedBy?.id && flaggedStudy?.resolvedAt;

  if (
    !hideFlagIssue &&
    !_.includes([STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE], status) &&
    !isEndOfDuration
  ) {
    arrayItem.push({
      label: t`Issue found last 6 hours`,
      value: (
        <div className="last-issue-found-container">
          <div>{generateIssueFound(artifact?.lastIssueFound, device.model)?.issueFound}</div>
          <NormalButton
            id="display-solution-modal-btn"
            className="view-solutions-btn"
            isText
            buttonName={t`View solutions`}
            onClick={toggleDisplaySolutionsDrawer}
          />
        </div>
      ),
    });
  }
  arrayItem.push(
    {
      label: t`Common issue found in study`,
      value: generateIssueFound(artifact?.commonIssueFound, device.model)?.issueFound,
    },
    {
      label: t`Total artifact percentage`,
      value: <div>{artifactPercentageRenderer(artifact?.percentage)}</div>,
    },
  );
  return (
    <div className="content">
      {isSkipArtifact && <UndoMarkAddressed studyId={id} />}
      {arrayItem.map((content) => renderRowItem(content))}
    </div>
  );
};

/**
 * Reshapes the study-details form values into the physician-and-diagnosis mutation input. Keeps interprettingPhysician and referringPhysician at the top level and nests primary, second, physiciansNote and patientMedications under diagnosis.
 *
 * @param payload - The flat form values from the study details editor.
 * @returns The mutation input for physicians and diagnosis.
 */
const convertPayload = (payload: any) => ({
  interprettingPhysician: payload.interprettingPhysician,
  referringPhysician: payload.referringPhysician,
  diagnosis: {
    primary: payload.primary,
    second: payload.second,
    physiciansNote: payload.physiciansNote,
    patientMedications: payload.patientMedications,
  },
});

/**
 * Builds the update-study mutation input from the study-settings form. Normalises the report due date to the start of its day, sends the primary study type when a follow-on type exists, attaches generateDefaultStudySetting-rebased settings with parsed thresholds and duration scaled by the day/hour unit for ongoing studies, adds or clears the follow-on block only while the study is unfinished, and keeps only id, reportDueDate and followOnStudy when couldNotEditStudyType is set.
 *
 * @param payload - The study-settings form values, including durations, types and units.
 * @param data - The current study, read for id, status, type, stop, linked studies and facility settings.
 * @param couldNotEditStudyType - When true, restricts the input to the report due date and follow-on block.
 * @returns The mutation input for the study update.
 */
const convertPayloadStudySetting = (payload: any, data: any, couldNotEditStudyType?: boolean) => {
  const {
    duration,
    reportDueDate,
    studyType,
    primaryStudyType,
    followOnStudyDuration,
    followOnStudyStudyType,
    followOnStudyReportDueDate,
    unitTime,
    unitTimeFO,
    ringId,
  } = payload;
  const isUnitDays = unitTime === 'day';
  const isUnitDaysFO = unitTimeFO === 'day';
  const input: {
    [x: string]: any;
    id: string;
  } = { id: data.id };
  const formattedReportDueDate = reportDueDate
    ? // reportDueDate is a valid moment from the date picker when truthy, so addTimezoneToMoment never returns null here
      addTimezoneToMoment({ dateMoment: reportDueDate })!.clone().startOf('day').toISOString()
    : new Date(0);
  const isFollowOnStudy = !_.isNil(followOnStudyStudyType);
  _.assign(input, {
    reportDueDate: formattedReportDueDate,
    studyType: isFollowOnStudy ? primaryStudyType : studyType,
  });
  let studySettingProps = _.cloneDeep(data.studySetting);
  if (data.status === STUDY_STATUS.ONGOING) {
    studySettingProps = generateDefaultStudySetting(
      data?.facility?.studySetting,
      studySettingProps,
      data?.studyType,
      studyType,
    );
    _.assign(input, {
      settings: {
        preEventTime: studySettingProps.preEventTime,
        postEventTime: studySettingProps.postEventTime,
        tachycardiaThreshold: parseInt(studySettingProps.tachycardiaThreshold, 10),
        bradycardiaThreshold: parseInt(studySettingProps.bradycardiaThreshold, 10),
        pauseLevel: (parseFloat as any)(studySettingProps.pauseLevel, 10),
        diagnosticLead: studySettingProps.diagnosticLead,
        afibDetection: studySettingProps.afibDetection,
        pauseDetection: studySettingProps.pauseDetection,
        duration: duration * (isUnitDays ? 1440 : 60),
        ringId,
      },
    });
  }
  const isEndOfDuration = dayjs().isAfter(dayjs(data?.stop));
  let formattedFollowOnStudyReportDueDate;
  if (
    data.status !== STUDY_STATUS.COMPLETED &&
    data.status !== STUDY_STATUS.DONE &&
    !isEndOfDuration
  ) {
    if (isFollowOnStudy) {
      // *: Apply for first follow-on study
      if (!data.linkedStudies?.length || data.id === data.linkedStudies[0].id) {
        formattedFollowOnStudyReportDueDate = followOnStudyReportDueDate
          ? // followOnStudyReportDueDate is a valid moment from the date picker when truthy, so addTimezoneToMoment never returns null here
            addTimezoneToMoment({ dateMoment: followOnStudyReportDueDate })!
              .clone()
              .startOf('day')
              .format('YYYY-MM-DD')
          : undefined;
        _.assign(input, {
          studyType: primaryStudyType,
          followOnStudy: {
            studyType: followOnStudyStudyType,
            duration: followOnStudyDuration * (isUnitDaysFO ? 1440 : 60),
            reportDueDate: formattedFollowOnStudyReportDueDate,
          },
        });
      }
    } else if (!_.isEmpty(data.info?.followOnStudy)) {
      _.assign(input, { followOnStudy: {} });
    }
  }
  if (couldNotEditStudyType) {
    return {
      id: input.id,
      reportDueDate: input.reportDueDate,
      followOnStudy: input.followOnStudy,
    };
  }
  return input;
};

/**
 * Renders the orange warning shown on the study details tab for draft or starting studies. Prints an info icon beside the message that study type and duration can only be changed once the study is on-going.
 *
 * @returns The draft-or-starting warning block.
 */
const renderDraftOrStartingContent = () => (
  <div className="warning">
    <img src={inforOrangeIcon} alt="inforOrangeIcon" />
    {t`To change study type or study duration, please hold until the study status is On-going.`}
  </div>
);

/**
 * Renders the orange warning shown while a device command is still being applied to the study. Prints the update-study message when the pending command is UPDATE_STUDY and the stopping-study message otherwise, next to an info icon.
 *
 * @param pendingCommand - The study's pending command, compared against STUDY_PENDING_COMMAND.UPDATE_STUDY.
 * @returns The pending-command warning block.
 */
const renderDevicePendingContent = (pendingCommand: string) => (
  <div className="warning">
    <img src={inforOrangeIcon} alt="inforOrangeIcon" />
    {pendingCommand === STUDY_PENDING_COMMAND.UPDATE_STUDY
      ? t`The study type and study duration cannot be changed because previous changes are currently being applied.`
      : 'Cannot update study type and study duration while the study is stopping.'}
  </div>
);

/**
 * Picks the warning shown in the study details message box.
 * Shows nothing for a completed study, the draft-or-starting notice for such a study, the
 * device-pending notice for the pending command when one is queued, and nothing otherwise.
 *
 * @param props - Flags draftOrStartingStudy, hasDevicePendingCommand and isCompletedStudy plus
 * the pendingCommand.
 * @returns The warning content, or null when none applies.
 */
const renderMessageBoxContent = ({
  draftOrStartingStudy,
  hasDevicePendingCommand,
  pendingCommand,
  isCompletedStudy,
}: {
  draftOrStartingStudy: boolean;
  hasDevicePendingCommand: boolean;
  pendingCommand: string;
  isCompletedStudy: boolean;
}) => {
  if (isCompletedStudy) {
    return null;
  }

  if (draftOrStartingStudy) {
    return renderDraftOrStartingContent();
  }

  if (hasDevicePendingCommand) {
    return renderDevicePendingContent(pendingCommand);
  }

  return null;
};

export {
  convertPayload,
  convertPayloadStudySetting,
  createTimelineData,
  foStudyContent,
  generateDiagnosis,
  generateHistoryData,
  generateInterpretingPhysician,
  generateReferringPhysician,
  generateStudyIssue,
  renderMessageBoxContent,
  renderRowItem,
};
